import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  changeSubInitiativeStatus,
  createSubInitiativeOnBehalf,
  getSubInitiativeCreators,
  getSubInitiatives,
  updateSubInitiative,
} from '../initiative.service';
import { resultMessage } from '../../../../../service/apiResult';
import InlineAlert from '../../../../components/inlineAlert';
import NotificationSnackbar from '../../../../components/notificationSnackbar';
import { detailPageCardSx as pageCardSx, detailSectionTitleSx } from '../../../../styles/detailStyles';
import {
  confirmationDialogSx,
  confirmationIconSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
  confirmationTextSx,
  confirmationTitleSx,
} from '../../../../styles/dialogStyles';

const headCellSx = { fontWeight: 600, color: '#1C1B1F', fontSize: 14, whiteSpace: 'nowrap' };

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');

const STATUS = {
  ACTIVE: { label: 'Active', color: 'success' },
  PAUSED: { label: 'Paused', color: 'warning' },
  CLOSED: { label: 'Closed', color: 'default' },
};

const KIND_LABEL = {
  SERVICE_PROVIDER: 'Service provider',
  INTEGRATOR: 'Integrator',
};

const ACTION_COPY = {
  ACTIVE: {
    title: 'Activate sub-initiative?',
    text: 'DAREs can assign farmers to it again.',
    button: 'Activate',
  },
  PAUSED: {
    title: 'Pause sub-initiative?',
    text: 'DAREs cannot assign new farmers while it is paused. Farmers already in it stay.',
    button: 'Pause',
  },
  CLOSED: {
    title: 'Close sub-initiative?',
    text: 'Closing ends all farmer memberships and cannot be undone.',
    button: 'Close',
  },
};

const emptyForm = {
  creator: null,
  name: '',
  description: '',
  countries: [],
  startDate: '',
  endDate: '',
};

// ISO date (yyyy-mm-dd) for a date input, from either a date or a date-time string.
const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

/**
 * Sub-initiatives under this Initiative. Approved players (service providers and initiative-based
 * integrators) create them from their own consoles; eKutir sees them all here, can create one on a
 * player's behalf, edit it, and pause, re-activate or close it. Creators see farmer counts only.
 */
export default function InitiativeSubInitiatives({ initiativeId, countries = [] }) {
  const [rows, setRows] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');

  // Create / edit dialog: `editing` is the row being edited, or null when creating.
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Status change: { row, status }
  const [statusChange, setStatusChange] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!initiativeId) return;
    setLoading(true);
    const [list, options] = await Promise.all([
      getSubInitiatives(initiativeId),
      getSubInitiativeCreators(initiativeId),
    ]);
    if (list.success) setRows(list.data || []);
    if (options.success) setCreators(options.data || []);
    if (!list.success || !options.success) {
      setError(resultMessage(list.success ? options : list, 'Could not load the sub-initiatives.'));
    }
    setLoading(false);
  }, [initiativeId]);

  useEffect(() => {
    load();
  }, [load]);

  const countryOptions = useMemo(
    () => (Array.isArray(countries) ? countries.filter((c) => c && c.id != null) : []),
    [countries]
  );

  // Why "Create on behalf" is unavailable, shown next to it so the admin knows what to do first.
  const cannotCreateReason = useMemo(() => {
    if (loading || error) return '';
    if (creators.length === 0) {
      return 'You can’t create a sub-initiative under this Initiative yet. Only a service provider or integrator '
        + 'that is an approved member of the Initiative can run one, and it has none. Once one is approved, '
        + 'it appears in the list here.';
    }
    if (countryOptions.length === 0) {
      return 'You can’t create a sub-initiative under this Initiative yet: it is not offered in any country. '
        + 'Add a country to the Initiative first.';
    }
    return '';
  }, [loading, error, creators.length, countryOptions.length]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, countries: countryOptions.length === 1 ? countryOptions : [] });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      creator: null,
      name: row.name || '',
      description: row.description || '',
      countries: (row.countries || []).map(
        (c) => countryOptions.find((option) => option.id === c.id) || c
      ),
      startDate: toDateInput(row.startDate),
      endDate: toDateInput(row.endDate),
    });
    setFormError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  const setField = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const validate = () => {
    if (!editing && !form.creator) return 'Choose who the sub-initiative is created for.';
    if (!form.name.trim()) return 'Enter a name.';
    if (form.countries.length === 0) return 'Choose at least one country.';
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      return 'The end date cannot be before the start date.';
    }
    return '';
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    setFormError('');
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      countryIds: form.countries.map((c) => c.id),
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };
    const res = editing
      ? await updateSubInitiative(editing.id, body)
      : await createSubInitiativeOnBehalf(initiativeId, { ...body, creatorUserId: form.creator.userId });
    setSaving(false);
    if (res.success) {
      setFormOpen(false);
      setSuccess(editing ? 'Sub-initiative updated' : `Sub-initiative created for ${form.creator.name}`);
      load();
    } else {
      setFormError(resultMessage(res));
    }
  };

  const closeStatus = () => {
    setStatusChange(null);
    setReason('');
  };

  const reasonRequired = statusChange && statusChange.status !== 'ACTIVE';

  const applyStatus = async () => {
    if (!statusChange) return;
    if (reasonRequired && !reason.trim()) return;
    const { row, status } = statusChange;
    setSaving(true);
    setError('');
    const res = await changeSubInitiativeStatus(row.id, status, reason.trim() || null);
    setSaving(false);
    closeStatus();
    if (res.success) {
      setSuccess(`${row.name} is now ${STATUS[status]?.label.toLowerCase() || status}`);
      load();
    } else {
      setError(resultMessage(res));
    }
  };

  const copy = statusChange ? ACTION_COPY[statusChange.status] : null;

  return (
    <Box sx={{ ...pageCardSx, mt: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2.5, justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}
      >
        <Box>
          <Typography sx={{ ...detailSectionTitleSx, mb: 0.5 }}>Sub-initiatives</Typography>
          <Typography sx={{ fontSize: 14, lineHeight: '20px', color: '#49454F' }}>
            Created under this Initiative by approved service providers and integrators. DAREs that took up the
            Initiative see its active sub-initiatives and assign farmers to them.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={openCreate}
          disabled={Boolean(cannotCreateReason) || creators.length === 0}
          startIcon={<Icon icon="mdi:plus" />}
          sx={{ ...confirmationPrimaryButtonSx, height: 40, flexShrink: 0 }}
        >
          Create on behalf
        </Button>
      </Stack>

      <InlineAlert message={error} onClose={() => setError('')} sx={{ mb: 2 }} />
      <InlineAlert message={cannotCreateReason} variant="warning" sx={{ mb: 2 }} />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Sub-initiative</TableCell>
              <TableCell sx={headCellSx}>Created by</TableCell>
              <TableCell sx={headCellSx}>Countries</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx} align="right">Active farmers</TableCell>
              <TableCell sx={headCellSx}>Dates</TableCell>
              <TableCell sx={headCellSx} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: '#49454F', py: 3, textAlign: 'center' }}>
                  {loading ? 'Loading…' : 'No sub-initiative has been created yet.'}
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const status = STATUS[row.status] || { label: row.status || '-', color: 'default' };
              const closed = row.status === 'CLOSED';
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{row.name}</Typography>
                    {row.description && (
                      <Typography sx={{ fontSize: 12, color: '#49454F', maxWidth: 320 }}>
                        {row.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14 }}>{row.creatorName || '-'}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {row.creatorKind && (
                        <Chip size="small" variant="outlined" label={KIND_LABEL[row.creatorKind] || row.creatorKind} />
                      )}
                      {row.createdOnBehalf && <Chip size="small" color="info" label="On behalf" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{(row.countries || []).map((c) => c.name).join(', ') || '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={status.color} label={status.label} />
                  </TableCell>
                  <TableCell align="right">{row.activeFarmers ?? 0}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(row.startDate)} – {formatDate(row.endDate)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {closed ? (
                      '-'
                    ) : (
                      <>
                        <Button
                          size="small"
                          disabled={saving}
                          onClick={() => openEdit(row)}
                          sx={{ textTransform: 'none' }}
                          startIcon={<Icon icon="mdi:pencil-outline" />}
                        >
                          Edit
                        </Button>
                        {row.status === 'ACTIVE' ? (
                          <Button
                            size="small"
                            disabled={saving}
                            onClick={() => setStatusChange({ row, status: 'PAUSED' })}
                            sx={{ textTransform: 'none' }}
                            startIcon={<Icon icon="mdi:pause" />}
                          >
                            Pause
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            disabled={saving}
                            onClick={() => setStatusChange({ row, status: 'ACTIVE' })}
                            sx={{ textTransform: 'none' }}
                            startIcon={<Icon icon="mdi:play" />}
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          size="small"
                          disabled={saving}
                          onClick={() => setStatusChange({ row, status: 'CLOSED' })}
                          sx={{ textTransform: 'none', color: '#DA342E' }}
                          startIcon={<Icon icon="mdi:close-circle-outline" />}
                        >
                          Close
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editing ? 'Edit sub-initiative' : 'Create sub-initiative on behalf of a player'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <InlineAlert message={formError} onClose={() => setFormError('')} />
            {editing ? (
              <TextField size="small" label="Created by" value={editing.creatorName || '-'} disabled />
            ) : (
              <TextField
                select
                size="small"
                label="Create for"
                value={form.creator ? form.creator.userId : ''}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    creator: creators.find((c) => c.userId === event.target.value) || null,
                  }))
                }
                helperText="Service providers and integrators approved on this Initiative"
              >
                {creators.map((c) => (
                  <MenuItem key={c.userId} value={c.userId}>
                    {c.name}
                    {c.kind ? ` · ${KIND_LABEL[c.kind] || c.kind}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              label="Name"
              value={form.name}
              onChange={setField('name')}
              inputProps={{ maxLength: 200 }}
              required
            />
            <TextField
              size="small"
              label="Description"
              value={form.description}
              onChange={setField('description')}
              multiline
              minRows={3}
              inputProps={{ maxLength: 2000 }}
            />
            <Autocomplete
              multiple
              options={countryOptions}
              value={form.countries}
              onChange={(_, value) => setForm((prev) => ({ ...prev, countries: value }))}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="The Initiative has no countries"
              renderInput={(params) => (
                <TextField {...params} size="small" label="Countries" helperText="Limited to the Initiative's countries" />
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start date"
                value={form.startDate}
                onChange={setField('startDate')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End date"
                value={form.endDate}
                onChange={setField('endDate')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: form.startDate || undefined }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeForm} disabled={saving} sx={confirmationSecondaryButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={confirmationPrimaryButtonSx}
          >
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={Boolean(statusChange)} onClose={closeStatus}>
        <Box sx={confirmationDialogSx}>
          <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Box sx={confirmationIconSx}>
              <Icon icon="proicons:alert-triangle" width="36" height="36" color="#C40000" />
            </Box>
            <Typography sx={confirmationTitleSx}>{copy?.title}</Typography>
            <Typography sx={confirmationTextSx}>
              <strong>{statusChange?.row.name}</strong>: {copy?.text}
            </Typography>
            <TextField
              fullWidth
              size="small"
              label={reasonRequired ? 'Reason (required)' : 'Reason (optional)'}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 500 }}
            />
            <Stack direction="row" spacing={1.5} sx={{ pt: 1, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={applyStatus}
                disabled={saving || (reasonRequired && !reason.trim())}
                sx={confirmationPrimaryButtonSx}
              >
                {copy?.button}
              </Button>
              <Button onClick={closeStatus} sx={confirmationSecondaryButtonSx}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <NotificationSnackbar
        open={Boolean(success)}
        onClose={() => setSuccess('')}
        message={success}
        severity="success"
        contentSx={{ px: 2.5 }}
      />
    </Box>
  );
}
